import React, { useState, useEffect } from "react";
import { X, Loader2, Save, Plus } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CATEGORIES = ["grocery", "beauty", "fashion", "restaurant", "electronics", "pharmacy", "other"];

const META_FIELDS = [
  { key: "hero_style", label: "Hero Style", ex: "split-cta / fullscreen-media / lookbook" },
  { key: "navbar_style", label: "Navbar Style", ex: "sticky-search / transparent-scroll / mega-menu" },
  { key: "footer_style", label: "Footer Style", ex: "wide-links / minimal-centered / compact-contact" },
  { key: "product_grid_style", label: "Product Grid", ex: "dense-4col / masonry / menu-list" },
  { key: "category_style", label: "Category Style", ex: "chip-row / editorial-cards / icon-grid" },
  { key: "banner_style", label: "Banner Style", ex: "carousel / hero-swap / editorial" },
];

/**
 * CreateEditTemplateDialog — dialog untuk create atau edit template.
 * Props:
 *   mode: "create" | "edit"
 *   template: initial data (kalau edit)
 *   open, onClose, onSaved
 */
export default function CreateEditTemplateDialog({ mode = "create", template, open, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      template_key: template?.template_key || "",
      template_name: template?.template_name || "",
      template_category: template?.template_category || "other",
      description: template?.description || "",
      preview_image: template?.preview_image || "",
      thumbnail: template?.thumbnail || "",
      version: template?.version || "1.0.0",
      is_active: template?.is_active ?? true,
      metadata: {
        hero_style: template?.metadata?.hero_style || "default",
        navbar_style: template?.metadata?.navbar_style || "default",
        footer_style: template?.metadata?.footer_style || "default",
        product_grid_style: template?.metadata?.product_grid_style || "default",
        category_style: template?.metadata?.category_style || "default",
        banner_style: template?.metadata?.banner_style || "default",
      },
    });
  }, [open, template]);

  if (!open || !form) return null;

  const set = (patch) => setForm({ ...form, ...patch });
  const setMeta = (k, v) => setForm({ ...form, metadata: { ...form.metadata, [k]: v } });

  const canSave = form.template_name.trim() && (isEdit || form.template_key.trim());

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        template_name: form.template_name.trim(),
        template_category: form.template_category,
        description: form.description,
        preview_image: form.preview_image,
        thumbnail: form.thumbnail,
        version: form.version,
        is_active: form.is_active,
        metadata: form.metadata,
      };
      let r;
      if (isEdit) {
        r = await api.put(`/platform/templates/${template.template_key}`, payload);
      } else {
        payload.template_key = form.template_key.trim().toLowerCase();
        r = await api.post("/platform/templates", payload);
      }
      toast.success(isEdit ? "Template disimpan." : `Template "${r.data.template.template_key}" dibuat.`);
      onSaved?.(r.data.template);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal menyimpan template"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="template-form-dialog">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3 sticky top-0 bg-[#0F0F17] z-10">
          <div className="text-white font-display font-black text-lg">{isEdit ? "Edit Template" : "Tambah Template Baru"}</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" data-testid="template-form-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Template Key *</label>
              <Input value={form.template_key} disabled={isEdit}
                onChange={(e) => set({ template_key: e.target.value.toLowerCase() })}
                placeholder="beauty-basic" className="bg-white/5 border-white/10 text-white font-mono disabled:opacity-60"
                data-testid="input-template-key" />
              {isEdit && <div className="text-[11px] text-white/40 mt-1">Immutable setelah dibuat.</div>}
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Template Name *</label>
              <Input value={form.template_name} onChange={(e) => set({ template_name: e.target.value })}
                placeholder="Beauty Basic" className="bg-white/5 border-white/10 text-white" data-testid="input-template-name" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Category</label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button key={c} type="button" onClick={() => set({ template_category: c })}
                    className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${
                      form.template_category === c ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                    }`} data-testid={`cat-option-${c}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Version (semver)</label>
              <Input value={form.version} onChange={(e) => set({ version: e.target.value })}
                placeholder="1.0.0" className="bg-white/5 border-white/10 text-white font-mono" data-testid="input-template-version" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Description</label>
            <Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2}
              className="bg-white/5 border-white/10 text-white" data-testid="input-template-description" />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Preview Image URL</label>
              <Input value={form.preview_image} onChange={(e) => set({ preview_image: e.target.value })}
                placeholder="https://…" className="bg-white/5 border-white/10 text-white text-xs" data-testid="input-template-preview" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Thumbnail URL</label>
              <Input value={form.thumbnail} onChange={(e) => set({ thumbnail: e.target.value })}
                placeholder="https://…" className="bg-white/5 border-white/10 text-white text-xs" data-testid="input-template-thumb" />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })}
              className="w-4 h-4 accent-emerald-500" data-testid="input-template-active" />
            <span className="text-xs text-white/80">Active</span>
          </label>

          <div>
            <div className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2">Metadata (Deployment Engine)</div>
            <div className="grid gap-2 md:grid-cols-2">
              {META_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-[10px] text-white/50 mb-1">{f.label}</label>
                  <Input value={form.metadata[f.key] || ""} onChange={(e) => setMeta(f.key, e.target.value)}
                    placeholder={f.ex} className="bg-white/5 border-white/10 text-white text-xs font-mono h-8" data-testid={`input-meta-${f.key}`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end sticky bottom-0 bg-[#0F0F17]">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={!canSave || saving} className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40" data-testid="template-form-submit">
            {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : (isEdit ? <><Save size={14} className="mr-1" /> Simpan</> : <><Plus size={14} className="mr-1" /> Buat Template</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
